import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Loader2, CreditCard, Download, Check } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { API_URL } from '../../lib/api';

const InvoicePage = () => {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const paid = searchParams.get('paid') === 'true';

  useEffect(() => {
    fetch(`${API_URL}/api/invoice/${invoiceId}`)
      .then(r => r.ok ? r.json() : null)
      .then(setInvoice)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [invoiceId]);

  const payWithStripe = async () => {
    try {
      const apiUrl = API_URL || '';
      const r = await fetch(`${apiUrl}/api/invoice/${invoiceId}/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin })
      });
      if (r.ok) {
        const data = await r.json();
        if (data.url) window.location.href = data.url;
        else toast.error('No payment URL returned');
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Payment failed');
      }
    } catch (e) {
      console.error('Payment error:', e);
      toast.error('Payment connection failed');
    }
  };

  const printInvoice = () => window.print();

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;
  if (!invoice) return <div className="min-h-screen flex items-center justify-center"><p className="text-slate-500">Invoice not found</p></div>;

  const sym = invoice.currency === 'NGN' ? '₦' : invoice.currency === 'USD' ? '$' : '£';
  const subtotal = (invoice.items || []).reduce((s, i) => s + (parseFloat(i.price) || 0) * (parseInt(i.units) || 1), 0);

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4 print:bg-white print:py-0">
      <Toaster position="top-center" />
      {/* Print/Pay buttons - hidden on print */}
      <div className="max-w-3xl mx-auto mb-4 flex gap-2 justify-end print:hidden">
        <Button variant="outline" onClick={printInvoice}><Download className="w-4 h-4 mr-2" /> Download PDF</Button>
        {invoice.status !== 'paid' && !paid && (
          <Button onClick={payWithStripe} className="bg-emerald-500 hover:bg-emerald-600 text-white"><CreditCard className="w-4 h-4 mr-2" /> Pay Online</Button>
        )}
      </div>

      {paid && (
        <div className="max-w-3xl mx-auto mb-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center print:hidden">
          <Check className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
          <p className="font-bold text-emerald-800">Payment Successful! Thank you.</p>
        </div>
      )}

      {/* Invoice */}
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg border p-8 print:shadow-none print:border-none print:rounded-none">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-2xl font-serif font-bold text-emerald-700">Creassion Resources</h1>
            <p className="text-sm text-slate-500">Nurturing Faith in Children</p>
          </div>
          <div className="text-right">
            <h2 className="text-3xl font-bold text-slate-800">INVOICE</h2>
            <p className="text-sm text-slate-500">Date: {invoice.date}</p>
            <p className="text-sm text-slate-500">{invoice.invoice_number}</p>
          </div>
        </div>

        {/* Customer */}
        <div className="mb-8">
          <p className="text-xs text-slate-400 uppercase mb-1">Bill To</p>
          <p className="font-bold text-slate-800">{invoice.customer_name}</p>
          {invoice.customer_address && <p className="text-sm text-slate-600 whitespace-pre-line">{invoice.customer_address}</p>}
          {invoice.customer_email && <p className="text-sm text-slate-500">{invoice.customer_email}</p>}
        </div>

        {/* Items Table */}
        <table className="w-full mb-6">
          <thead>
            <tr className="border-b-2 border-emerald-500">
              <th className="text-left py-3 text-sm font-bold text-slate-700">Description</th>
              <th className="text-right py-3 text-sm font-bold text-slate-700">Price</th>
              <th className="text-center py-3 text-sm font-bold text-slate-700">Units</th>
              <th className="text-right py-3 text-sm font-bold text-slate-700">Total</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.items || []).map((item, i) => {
              const lineTotal = (parseFloat(item.price) || 0) * (parseInt(item.units) || 1);
              return (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-3 text-sm text-slate-700">{item.description}</td>
                  <td className="py-3 text-sm text-right text-slate-700">{sym}{parseFloat(item.price || 0).toLocaleString()}</td>
                  <td className="py-3 text-sm text-center text-slate-700">{item.units || 1}</td>
                  <td className="py-3 text-sm text-right font-medium text-slate-800">{sym}{lineTotal.toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end mb-8">
          <div className="w-64 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span>{sym}{subtotal.toLocaleString()}</span></div>
            {(parseFloat(invoice.discount) || 0) > 0 && <div className="flex justify-between text-sm"><span className="text-red-500">Discount</span><span>-{sym}{parseFloat(invoice.discount).toLocaleString()}</span></div>}
            {(parseFloat(invoice.tax) || 0) > 0 && <div className="flex justify-between text-sm"><span className="text-slate-500">Tax</span><span>+{sym}{parseFloat(invoice.tax).toLocaleString()}</span></div>}
            <div className="flex justify-between font-bold text-lg border-t-2 border-emerald-500 pt-2"><span>Total</span><span>{sym}{(invoice.total || 0).toLocaleString()}</span></div>
          </div>
        </div>

        {/* Bank Details */}
        <div className="bg-slate-50 rounded-xl p-5 mb-6">
          <p className="font-bold text-slate-800 mb-2">Payment Details</p>
          <p className="text-sm text-slate-600 mb-3">Kindly make payment to the account details below:</p>
          {invoice.currency === 'NGN' ? (
            <div className="text-sm text-slate-700 space-y-1">
              <p><strong>Account Name:</strong> Jadesola Ayoola</p>
              <p><strong>Bank:</strong> Kuda Bank</p>
              <p><strong>Account Number:</strong> 2063547946</p>
            </div>
          ) : (
            <div className="text-sm text-slate-700 space-y-1">
              <p><strong>Account Name:</strong> Creassion Resources</p>
              <p><strong>Account Number:</strong> 67455779</p>
              <p><strong>Sort Code:</strong> 08-92-99</p>
            </div>
          )}
        </div>

        {/* Notes */}
        {invoice.notes && <p className="text-sm text-slate-600 italic mb-6">{invoice.notes}</p>}

        {/* Footer */}
        <div className="border-t pt-4 text-center text-sm text-slate-500">
          <p>Thank you for your support and patronage.</p>
          <p className="mt-1">info@creassion.org | www.creassion.org</p>
        </div>
      </div>
    </div>
  );
};

export default InvoicePage;
